import React, { useState, useEffect, useCallback } from 'react';
import { BottomSheet, StatusActionButton, WorkTypeSelector } from '../../../shared/ui';
import {
  contractsV2Api,
  ContractV2,
  UpdateContractRequestV2,
  ContractParticipantRequestV2
} from '../../../api/contractsV2Api';
import { siteExecutorsApi, SiteExecutor } from '../../../api/siteExecutorsApi';
import './ContractsCard.css';

interface EditContractModalProps {
  isOpen: boolean;
  siteId: number;
  contractId: number | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface SelectedParticipant {
  executor: SiteExecutor | { id: number; displayName: string };
  sharePercent: string;
}

const EditContractModal: React.FC<EditContractModalProps> = ({
  isOpen,
  siteId,
  contractId,
  onClose,
  onSuccess
}) => {
  const [contract, setContract] = useState<ContractV2 | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [typeOfWorkId, setTypeOfWorkId] = useState<number | null>(null);
  const [fixedAmount, setFixedAmount] = useState<string>('');
  const [deductionPercent, setDeductionPercent] = useState<string>('0');
  const [executors, setExecutors] = useState<SiteExecutor[]>([]);
  const [participants, setParticipants] = useState<SelectedParticipant[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const loadContract = useCallback(async () => {
    if (!siteId || !contractId) return;
    setLoadError(null);
    try {
      const data = await contractsV2Api.getSiteContract(siteId, contractId);
      setContract(data);
      setTitle(data.title);
      setDescription(data.description || '');
      setTypeOfWorkId(data.typeOfWorkId);
      setFixedAmount(data.fixedAmount.toString());
      setDeductionPercent(String(data.deductionPercent ?? 0));
      if (data.participants && data.participants.length > 0) {
        setParticipants(
          data.participants.map((p) => ({
            executor: {
              id: p.siteExecutorId,
              displayName: p.displayName || p.userName || `Исполнитель #${p.siteExecutorId}`
            } as { id: number; displayName: string },
            sharePercent: String(p.sharePercent)
          }))
        );
      } else {
        setParticipants([]);
      }
    } catch {
      setLoadError('Не удалось загрузить контракт');
      setContract(null);
    }
  }, [siteId, contractId]);

  const loadExecutors = useCallback(async () => {
    if (!siteId) return;
    try {
      const data = await siteExecutorsApi.getSiteExecutors(siteId);
      setExecutors(data);
    } catch {
      setExecutors([]);
    }
  }, [siteId]);

  useEffect(() => {
    if (isOpen && contractId) {
      loadContract();
      loadExecutors();
    }
  }, [isOpen, contractId, loadContract, loadExecutors]);

  const addParticipant = (executor: SiteExecutor) => {
    if (participants.some((p) => (p.executor as { id: number }).id === executor.id)) return;
    setParticipants((prev) => [...prev, { executor, sharePercent: '0' }]);
  };

  const removeParticipant = (executorId: number) => {
    setParticipants((prev) => prev.filter((p) => (p.executor as { id: number }).id !== executorId));
  };

  const updateParticipantShare = (executorId: number, value: string) => {
    setParticipants((prev) =>
      prev.map((p) =>
        (p.executor as { id: number }).id === executorId ? { ...p, sharePercent: value } : p
      )
    );
  };

  const totalSharePercent = participants.reduce((sum, p) => {
    const v = parseFloat(p.sharePercent.replace(',', '.')) || 0;
    return sum + v;
  }, 0);

  const participantsValid = participants.length === 0 || (totalSharePercent > 0 && totalSharePercent <= 100.01);

  const getParticipantsPayload = (): ContractParticipantRequestV2[] => {
    return participants.map((p) => ({
      siteExecutorId: (p.executor as { id: number }).id,
      sharePercent: parseFloat(p.sharePercent.replace(',', '.')) || 0
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contractId || !title.trim() || !typeOfWorkId) return;
    const amount = parseFloat(fixedAmount.replace(',', '.'));
    if (isNaN(amount) || amount <= 0) {
      setSubmitError('Введите корректную сумму');
      return;
    }
    const percent = parseFloat(deductionPercent.replace(',', '.')) || 0;
    if (isNaN(percent) || percent < 0 || percent > 100) {
      setSubmitError('Процент вычета должен быть от 0 до 100');
      return;
    }
    if (!participantsValid) {
      setSubmitError('Сумма долей участников не должна превышать 100%');
      return;
    }

    try {
      setLoading(true);
      setSubmitError(null);
      const data: UpdateContractRequestV2 = {
        title: title.trim(),
        description: description.trim() || undefined,
        typeOfWorkId,
        fixedAmount: amount,
        deductionPercent: percent,
        participants: getParticipantsPayload()
      };
      await contractsV2Api.updateContract(siteId, contractId, data);
      onSuccess();
      onClose();
    } catch (err: unknown) {
      console.error('Error updating contract:', err);
      const res =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: unknown } }).response
          : null;
      let msg: string | null = null;
      if (res?.data) {
        const d = res.data;
        if (typeof d === 'string') msg = d;
        else if (d && typeof d === 'object') {
          const obj = d as Record<string, unknown>;
          msg =
            [obj.message, obj.detail, obj.title].find((v) => typeof v === 'string') as
              | string
              | undefined ?? null;
        }
      }
      setSubmitError(msg || 'Не удалось обновить контракт');
    } finally {
      setLoading(false);
    }
  };

  const getExecutorDisplayName = (p: SelectedParticipant) => {
    const ex = p.executor as { id: number; displayName?: string };
    const found = executors.find((e) => e.id === ex.id);
    return found?.displayName ?? ex.displayName ?? `Исполнитель #${ex.id}`;
  };

  if (!isOpen) return null;

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title="Редактировать контракт" size="medium" showHandle={true}>
      <form onSubmit={handleSubmit} className="create-contract-form">
        {loadError && (
          <div className="create-contract-error">
            <i className="ri-error-warning-line"></i>
            <span>{loadError}</span>
          </div>
        )}
        {submitError && (
          <div className="create-contract-error">
            <i className="ri-error-warning-line"></i>
            <span>{submitError}</span>
          </div>
        )}
        {contract && !loadError && (
          <>
            <div className="form-group">
              <label>Название *</label>
              <input
                type="text"
                placeholder="Название контракта"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Описание</label>
              <textarea
                placeholder="Дополнительная информация"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
              />
            </div>
            <div className="form-group">
              <WorkTypeSelector
                label="Вид работы *"
                value={typeOfWorkId}
                onChange={setTypeOfWorkId}
                required
                placeholder="Выберите вид работы"
              />
            </div>
            <div className="form-group">
              <label>Сумма контракта (₽) *</label>
              <input
                type="text"
                inputMode="decimal"
                placeholder="0"
                value={fixedAmount}
                onChange={(e) => setFixedAmount(e.target.value.replace(/[^\d.,]/g, ''))}
              />
            </div>
            <div className="form-group">
              <label>Процент вычета (%)</label>
              <input
                type="text"
                inputMode="decimal"
                placeholder="0"
                value={deductionPercent}
                onChange={(e) => setDeductionPercent(e.target.value.replace(/[^\d.,]/g, '') || '0')}
              />
            </div>
            <div className="form-group contract-participants-section">
              <label>Участники контракта</label>
              <span className="form-hint">Добавьте или удалите исполнителей, укажите долю каждого (сумма до 100%)</span>
              <select
                className="contract-participant-add"
                value=""
                onChange={(e) => {
                  const id = Number(e.target.value);
                  if (id) addParticipant(executors.find((ex) => ex.id === id)!);
                  e.target.value = '';
                }}
              >
                <option value="">+ Добавить участника</option>
                {executors
                  .filter((ex) => !participants.some((p) => (p.executor as { id: number }).id === ex.id))
                  .map((ex) => (
                    <option key={ex.id} value={ex.id}>
                      {ex.displayName} {ex.isExternal ? '(внешний)' : ''}
                    </option>
                  ))}
              </select>
              {participants.length > 0 && (
                <div className="contract-participants-list">
                  {participants.map((p) => (
                    <div key={(p.executor as { id: number }).id} className="contract-participant-row">
                      <span className="participant-name">{getExecutorDisplayName(p)}</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        placeholder="%"
                        value={p.sharePercent}
                        onChange={(e) =>
                          updateParticipantShare((p.executor as { id: number }).id, e.target.value.replace(/[^\d.,]/g, ''))
                        }
                        className="participant-share-input"
                      />
                      <button
                        type="button"
                        className="participant-remove"
                        onClick={() => removeParticipant((p.executor as { id: number }).id)}
                        title="Удалить"
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    </div>
                  ))}
                  <div className="contract-participants-total">
                    Итого: {totalSharePercent.toFixed(1)}%
                    {totalSharePercent > 100 && <span className="participants-error"> — превышает 100%</span>}
                  </div>
                </div>
              )}
            </div>
            <div className="create-contract-actions">
              <StatusActionButton type="button" variant="secondary" onClick={onClose} disabled={loading}>
                Отмена
              </StatusActionButton>
              <StatusActionButton
                type="submit"
                variant="primary"
                disabled={loading || !title.trim() || !typeOfWorkId || !fixedAmount || !participantsValid}
              >
                {loading ? 'Сохранение...' : 'Сохранить'}
              </StatusActionButton>
            </div>
          </>
        )}
      </form>
    </BottomSheet>
  );
};

export default EditContractModal;
